import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DndContext,
    closestCorners,
    pointerWithin,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    defaultDropAnimationSideEffects,
    useDroppable,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useSnapshotShare from '../../hooks/useSnapshotShare';
import useSnapshotHistory from '../../hooks/useSnapshotHistory';
import SnapshotToast from '../../components/SnapshotToast';
import HistoryDrawer from '../../components/HistoryDrawer';
import ConfirmationModal from '../../components/ConfirmationModal';
import ThoughtBubbleIcon from '../../components/ThoughtBubbleIcon';
import { Plus, Share2, Home, Trash2, GripVertical, Pencil, AlertCircle, Calendar, Users, XCircle, CornerDownLeft, Clock } from 'lucide-react';


// --- Components ---

const TaskCard = ({ id, content, onDelete, onUpdate }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(content);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        touchAction: 'none',
    };

    const handleSave = () => {
        if (editContent.trim()) {
            onUpdate(id, editContent.trim());
        } else {
            setEditContent(content);
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={`bg-white p-3 rounded-lg shadow-sm border border-gray-200 group hover:shadow-md transition-shadow mb-2 flex items-start gap-2 cursor-grab active:cursor-grabbing ${isDragging ? 'ring-2 ring-blue-400 rotate-2' : ''}`}
        >
            <div className="mt-1 text-gray-400 hover:text-gray-600">
                <GripVertical size={14} />
            </div>

            {isEditing ? (
                <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="flex-grow text-sm text-gray-800 border rounded p-1 focus:ring-2 focus:ring-blue-500 outline-none resize-none bg-gray-50"
                    autoFocus
                    rows={Math.max(2, Math.ceil(editContent.length / 30))}
                    onClick={(e) => e.stopPropagation()}
                />
            ) : (
                <p className="flex-grow text-gray-800 text-sm leading-relaxed break-words pointer-events-none">{content}</p>
            )}

            <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsEditing(true);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="text-gray-300 hover:text-blue-500 transition-colors"
                >
                    <Pencil size={12} />
                </button>
            </div>
        </div>
    );
};

const MatrixQuadrant = ({ id, title, subtitle, icon: Icon, color, tasks, onDeleteTask, onUpdateTask, onClear }) => {
    const { setNodeRef } = useDroppable({ id });

    // Color mappings for headers
    const headerColors = {
        green: "bg-green-100 text-green-800 border-green-200",
        blue: "bg-blue-100 text-blue-800 border-blue-200",
        yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
        red: "bg-red-100 text-red-800 border-red-200",
    };

    return (
        <div ref={setNodeRef} className="flex flex-col h-full bg-gray-50/50 rounded-xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className={`p-3 border-b ${headerColors[color]} flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                    <Icon size={16} />
                    <div>
                        <h3 className="font-bold text-sm uppercase tracking-wide">{title}</h3>
                        <p className="text-[10px] opacity-80 font-medium">{subtitle}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="bg-white/50 px-2 py-0.5 rounded-full text-xs font-bold">{tasks.length}</span>
                    {onClear && tasks.length > 0 && (
                        <button
                            onClick={onClear}
                            className="p-1 hover:bg-red-200 rounded text-red-600 transition-colors"
                            title="Clear All"
                        >
                            <Trash2 size={14} />
                        </button>
                    )}
                </div>
            </div>

            {/* List */}
            <div className="flex-grow p-2 overflow-y-auto min-h-[150px]">
                <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                    {tasks.map((task) => (
                        <TaskCard key={task.id} id={task.id} content={task.content} onDelete={onDeleteTask} onUpdate={onUpdateTask} />
                    ))}
                </SortableContext>
                {tasks.length === 0 && (
                    <div className="h-full flex items-center justify-center text-gray-300 text-xs italic">
                        Drop items here
                    </div>
                )}
            </div>
        </div>
    );
};

const TrashZone = () => {
    const { setNodeRef, isOver } = useDroppable({ id: 'trash-zone' });
    return (
        <div
            ref={setNodeRef}
            className={`w-full p-3 mb-2 rounded-xl border-2 border-dashed transition-colors flex items-center justify-center gap-2 ${isOver ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 text-gray-400 bg-gray-50/50'
                }`}
        >
            <Trash2 size={18} className="pointer-events-none" />
            <span className="text-sm font-medium pointer-events-none">Drag here to delete</span>
        </div>
    );
};

const DroppableTab = ({ id, label, color, bg, isActive, onClick }) => {
    const { setNodeRef, isOver } = useDroppable({ id: `tab-${id}` });
    return (
        <button
            ref={setNodeRef}
            onClick={onClick}
            className={`flex-1 py-2 px-1 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${isActive ? `${color} ${bg}` :
                isOver ? 'bg-blue-100 border-blue-400 text-blue-600' :
                    'text-gray-400 border-transparent hover:bg-gray-100'
                }`}
        >
            {label}
        </button>
    );
};

// --- Main App ---
const LOCAL_STORAGE_KEY = 'priorityMatrix';

const PriorityMatrixApp = () => {
    const navigate = useNavigate();
    const [quadrants, setQuadrants] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('s')) {
            return {
                do_now: [{ id: 't1', content: 'Finish the project 🚀' }],
                plan: [{ id: 't2', content: 'Weekly review' }],
                delegate: [{ id: 't3', content: 'Email responses' }],
                eliminate: [{ id: 't4', content: 'Doomscrolling' }],
            };
        }
        try {
            const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (stored) return JSON.parse(stored);
        } catch (e) {
            console.error("Failed to load matrix data", e);
        }
        return {
            do_now: [{ id: 't1', content: 'Finish the project 🚀' }],
            plan: [{ id: 't2', content: 'Weekly review' }],
            delegate: [{ id: 't3', content: 'Email responses' }],
            eliminate: [{ id: 't4', content: 'Doomscrolling' }],
        };
    });

    const [inputValue, setInputValue] = useState('');
    const [activeId, setActiveId] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);

    // Mobile Tab State
    const [activeTab, setActiveTab] = useState('do_now');

    // Save to LocalStorage (only if not snapshot)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (!params.get('s')) {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(quadrants));
        }
    }, [quadrants]);

    // Use shared snapshot hook (Orange theme)
    const { handleShare, showToast, setShowToast } = useSnapshotShare(quadrants, setQuadrants, 's');

    // Snapshot History Hook
    const { history, saveSnapshot, restoreSnapshot, deleteSnapshot, clearHistory } = useSnapshotHistory('priorityMatrix', quadrants);

    const handleShareWithSnapshot = () => {
        saveSnapshot('Shared via Link');
        handleShare();
    };

    const handleRestore = (id) => {
        const data = restoreSnapshot(id);
        if (data) {
            setQuadrants(data);
            setIsHistoryOpen(false);
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const submitTask = () => {
        if (inputValue.trim()) {
            const newTask = { id: `task-${Date.now()}`, content: inputValue.trim() };
            // Add to currently active tab on mobile, or 'do_now' on desktop default
            const target = window.innerWidth < 768 ? activeTab : 'do_now';

            setQuadrants(prev => ({
                ...prev,
                [target]: [newTask, ...prev[target]],
            }));
            setInputValue('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') submitTask();
    };

    const handleDeleteTask = (taskId) => {
        setQuadrants(prev => {
            const newQ = { ...prev };
            Object.keys(newQ).forEach(key => {
                newQ[key] = newQ[key].filter(t => t.id !== taskId);
            });
            return newQ;
        });
    };

    const handleUpdateTask = (taskId, newContent) => {
        setQuadrants(prev => {
            const newQ = { ...prev };
            Object.keys(newQ).forEach(key => {
                newQ[key] = newQ[key].map(t => t.id === taskId ? { ...t, content: newContent } : t);
            });
            return newQ;
        });
    };

    const handleClearEliminate = () => {
        if (window.confirm("Clear all 'Eliminate' tasks?")) {
            setQuadrants(prev => ({ ...prev, eliminate: [] }));
        }
    };

    const handleClearMatrix = () => {
        setIsClearModalOpen(true);
    };

    const confirmClearMatrix = () => {
        setQuadrants({
            do_now: [],
            plan: [],
            delegate: [],
            eliminate: []
        });
        setIsClearModalOpen(false);
    };

    // --- DnD Logic ---
    // --- DnD Logic ---
    const findContainer = (id) => {
        if (id in quadrants) return id;
        return Object.keys(quadrants).find(key => quadrants[key].find(t => t.id === id));
    };

    const handleDragStart = (event) => setActiveId(event.active.id);

    const handleDragOver = (event) => {
        try {
            const { active, over } = event;
            const overId = over?.id;
            if (!overId || active.id === overId) return;

            // Handle Trash Zone Hover
            if (overId === 'trash-zone') return;

            // Handle Tab Hover (Mobile)
            if (overId.startsWith('tab-')) return;

            const activeContainer = findContainer(active.id);
            const overContainer = findContainer(overId);

            if (!activeContainer || !overContainer || activeContainer === overContainer) return;

            setQuadrants((prev) => {
                const activeItems = prev[activeContainer];
                const overItems = prev[overContainer];
                const activeIndex = activeItems.findIndex((t) => t.id === active.id);
                const overIndex = overItems.findIndex((t) => t.id === overId);

                if (activeIndex === -1) return prev;

                let newIndex;
                if (overId in prev) {
                    newIndex = overItems.length + 1;
                } else {
                    const isBelowOverItem = over && active.rect.current.translated && active.rect.current.translated.top > over.rect.top + over.rect.height;
                    const modifier = isBelowOverItem ? 1 : 0;
                    newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
                }

                return {
                    ...prev,
                    [activeContainer]: [...prev[activeContainer].filter((item) => item.id !== active.id)],
                    [overContainer]: [
                        ...prev[overContainer].slice(0, newIndex),
                        activeItems[activeIndex],
                        ...prev[overContainer].slice(newIndex, prev[overContainer].length),
                    ],
                };
            });
        } catch (error) {
            console.error("Drag Over Error:", error);
        }
    };

    const handleDragEnd = (event) => {
        try {
            const { active, over } = event;

            if (!over) {
                setActiveId(null);
                return;
            }

            // Handle Trash Zone Drop
            if (over.id === 'trash-zone') {
                handleDeleteTask(active.id);
                setActiveId(null);
                return;
            }

            // Handle Tab Drop (Mobile Move)
            if (over.id.startsWith('tab-')) {
                const targetQuadrant = over.id.replace('tab-', '');
                const activeContainer = findContainer(active.id);

                if (activeContainer && activeContainer !== targetQuadrant) {
                    setQuadrants(prev => {
                        const task = prev[activeContainer].find(t => t.id === active.id);
                        if (!task) return prev;

                        return {
                            ...prev,
                            [activeContainer]: prev[activeContainer].filter(t => t.id !== active.id),
                            [targetQuadrant]: [task, ...prev[targetQuadrant]] // Add to top
                        };
                    });
                    setActiveTab(targetQuadrant); // Switch view to where we dropped it
                }
                setActiveId(null);
                return;
            }

            const activeContainer = findContainer(active.id);
            const overContainer = findContainer(over?.id);

            if (activeContainer && overContainer && activeContainer === overContainer) {
                const activeIndex = quadrants[activeContainer].findIndex((t) => t.id === active.id);
                const overIndex = quadrants[overContainer].findIndex((t) => t.id === over.id);
                if (activeIndex !== overIndex) {
                    setQuadrants((prev) => ({
                        ...prev,
                        [activeContainer]: arrayMove(prev[activeContainer], activeIndex, overIndex),
                    }));
                }
            }
        } catch (error) {
            console.error("Drag End Error:", error);
        } finally {
            setActiveId(null);
        }
    };

    const activeTask = activeId ? (
        quadrants.do_now.find(t => t.id === activeId) ||
        quadrants.plan.find(t => t.id === activeId) ||
        quadrants.delegate.find(t => t.id === activeId) ||
        quadrants.eliminate.find(t => t.id === activeId)
    ) : null;

    // --- Render Helpers ---
    const tabs = [
        { id: 'do_now', label: 'Do Now', color: 'text-green-600 border-green-500', bg: 'bg-green-50' },
        { id: 'plan', label: 'Plan', color: 'text-blue-600 border-blue-500', bg: 'bg-blue-50' },
        { id: 'delegate', label: 'Delegate', color: 'text-yellow-600 border-yellow-500', bg: 'bg-yellow-50' },
        { id: 'eliminate', label: 'Eliminate', color: 'text-red-600 border-red-500', bg: 'bg-red-50' },
    ];

    // Custom Collision Detection to prioritize Trash and Tabs
    const collisionDetectionStrategy = (args) => {
        // 1. Check if pointer is over Trash Zone
        const pointerCollisions = pointerWithin(args);
        const trashCollision = pointerCollisions.find(c => c.id === 'trash-zone');
        if (trashCollision) return [trashCollision];

        // 2. Check if pointer is over a Tab (Mobile)
        const tabCollision = pointerCollisions.find(c => c.id.toString().startsWith('tab-'));
        if (tabCollision) return [tabCollision];

        // 3. Fallback to closest corners for sorting
        return closestCorners(args);
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans text-gray-900">
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 md:px-6 py-4 flex items-center justify-between sticky top-0 z-50">
                <div
                    className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity w-1/3"
                    onClick={() => navigate('/')}
                >
                    <ThoughtBubbleIcon className="fill-blue-600 text-blue-600 shrink-0" size={24} />
                    <span className="text-sm md:text-xl font-bold md:font-extrabold text-blue-600 tracking-tight font-rounded">
                        Thoughtblink
                    </span>
                </div>

                <div className="flex flex-col items-center justify-center w-1/3">
                    <h1 className="text-lg md:text-xl font-bold bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent leading-tight">
                        Prioritize-It
                    </h1>
                    <span className="text-[9px] md:text-[10px] text-slate-400 font-medium tracking-wide uppercase whitespace-nowrap">
                        Eisenhower Matrix
                    </span>
                </div>

                <div className="flex justify-end items-center gap-4 w-1/3">
                    <button
                        onClick={handleShareWithSnapshot}
                        className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-full transition-all"
                        title="Share Matrix"
                    >
                        <Share2 size={20} />
                    </button>
                    <button
                        onClick={() => setIsHistoryOpen(true)}
                        className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-all"
                        title="Version History"
                    >
                        <Clock size={20} />
                    </button>
                    <button
                        onClick={handleClearMatrix}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                        title="Clear Matrix"
                    >
                        <Trash2 size={20} />
                    </button>
                </div>
            </header>

            <main className="flex-grow flex flex-col p-4 max-w-6xl mx-auto w-full gap-4 h-[calc(100vh-64px)]">

                <DndContext
                    sensors={sensors}
                    collisionDetection={collisionDetectionStrategy}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                >
                    {/* Trash Zone (Mobile/Desktop) */}
                    <TrashZone />

                    {/* Input */}
                    <div className="w-full max-w-2xl mx-auto relative group shrink-0">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Plus className="text-slate-400" size={24} />
                        </div>
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={`Add to "${tabs.find(t => t.id === activeTab)?.label}"...`}
                            className="w-full pl-12 pr-12 py-4 bg-white border-2 border-slate-200 rounded-xl text-lg shadow-sm placeholder-slate-400 focus:border-slate-400 focus:outline-none transition-all"
                        />
                        <button
                            onClick={submitTask}
                            className="absolute inset-y-0 right-4 flex items-center justify-center text-slate-400 hover:text-blue-600 cursor-pointer transition-colors"
                            title="Add Task"
                        >
                            <CornerDownLeft size={20} />
                        </button>
                    </div>

                    {/* Mobile Tabs (Droppable) */}
                    <div className="md:hidden flex gap-1 overflow-x-auto no-scrollbar shrink-0 pb-2">
                        {tabs.map(tab => (
                            <DroppableTab
                                key={tab.id}
                                {...tab}
                                isActive={activeTab === tab.id}
                                onClick={() => setActiveTab(tab.id)}
                            />
                        ))}
                    </div>

                    {/* Matrix Grid (Desktop) / Active Tab (Mobile) */}
                    <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-4 min-h-0">
                        {/* Quadrant 1: Do Now */}
                        <div className={`h-full min-h-0 ${activeTab === 'do_now' ? 'block' : 'hidden md:block'}`}>
                            <MatrixQuadrant
                                id="do_now"
                                title="Do Now"
                                subtitle="Urgent & Important"
                                icon={AlertCircle}
                                color="green"
                                tasks={quadrants.do_now}
                                onDeleteTask={handleDeleteTask}
                                onUpdateTask={handleUpdateTask}
                            />
                        </div>

                        {/* Quadrant 2: Plan */}
                        <div className={`h-full min-h-0 ${activeTab === 'plan' ? 'block' : 'hidden md:block'}`}>
                            <MatrixQuadrant
                                id="plan"
                                title="Plan"
                                subtitle="Not Urgent & Important"
                                icon={Calendar}
                                color="blue"
                                tasks={quadrants.plan}
                                onDeleteTask={handleDeleteTask}
                                onUpdateTask={handleUpdateTask}
                            />
                        </div>

                        {/* Quadrant 3: Delegate */}
                        <div className={`h-full min-h-0 ${activeTab === 'delegate' ? 'block' : 'hidden md:block'}`}>
                            <MatrixQuadrant
                                id="delegate"
                                title="Delegate"
                                subtitle="Urgent & Not Important"
                                icon={Users}
                                color="yellow"
                                tasks={quadrants.delegate}
                                onDeleteTask={handleDeleteTask}
                                onUpdateTask={handleUpdateTask}
                            />
                        </div>

                        {/* Quadrant 4: Eliminate */}
                        <div className={`h-full min-h-0 ${activeTab === 'eliminate' ? 'block' : 'hidden md:block'}`}>
                            <MatrixQuadrant
                                id="eliminate"
                                title="Eliminate"
                                subtitle="Not Urgent & Not Important"
                                icon={XCircle}
                                color="red"
                                tasks={quadrants.eliminate}
                                onDeleteTask={handleDeleteTask}
                                onUpdateTask={handleUpdateTask}
                                onClear={handleClearEliminate}
                            />
                        </div>
                    </div>

                    <DragOverlay>
                        {activeTask ? (
                            <div className="bg-white p-3 rounded-lg shadow-xl border border-blue-500/50 rotate-2 cursor-grabbing opacity-90 w-[280px]">
                                <p className="text-gray-800 text-sm">{activeTask.content}</p>
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </main>
            <SnapshotToast isVisible={showToast} onClose={() => setShowToast(false)} color="orange" />
            <HistoryDrawer
                isOpen={isHistoryOpen}
                onClose={() => setIsHistoryOpen(false)}
                history={history}
                onRestore={handleRestore}
                onDelete={deleteSnapshot}
                onClearHistory={clearHistory}
            />
            <ConfirmationModal
                isOpen={isClearModalOpen}
                onClose={() => setIsClearModalOpen(false)}
                onConfirm={confirmClearMatrix}
                title="Clear Priority Matrix?"
                message="Are you sure you want to delete all tasks across all quadrants? This action cannot be undone."
                confirmText="Yes, Clear All"
            />
        </div>
    );
};

export default PriorityMatrixApp;
